import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { SectionHeading, FadeUp } from "./Reveal";
import { api } from "../lib/api";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";

const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    try {
      await api.post("/newsletter", { email });
      setState("done");
    } catch {
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <div data-testid="newsletter-success" className="flex items-center justify-center gap-3 text-jx-ink">
        <CheckCircle2 className="w-5 h-5 text-jx-magenta" />
        <p className="font-medium">You're on the list — check your inbox for a welcome email.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="newsletter-form" className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 max-w-xl mx-auto">
      <div className="flex-1 flex items-center gap-3 rounded-full bg-white border border-jx-ink/10 px-5 focus-within:border-jx-purple transition-colors">
        <Mail className="w-4 h-4 text-jx-ink/35 shrink-0" />
        <input
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="newsletter-email-input"
          placeholder="Your work email"
          className="w-full bg-transparent outline-none py-3.5 text-base text-jx-ink placeholder:text-jx-ink/35"
        />
      </div>
      <button
        type="submit"
        disabled={state === "loading"}
        data-testid="newsletter-subscribe-button"
        className="rounded-full jx-gradient-bg text-white font-medium px-7 py-3.5 transition-transform duration-300 hover:scale-[1.03] active:scale-95 disabled:opacity-60 inline-flex items-center justify-center gap-2"
      >
        {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Subscribe"}
      </button>
      {state === "error" && <p data-testid="newsletter-error" className="text-sm text-red-600 sm:absolute sm:-bottom-7">Something went wrong — please try again.</p>}
    </form>
  );
};

const ARTICLES = [
  {
    category: "Agentic AI",
    title: "What Agentic AI Changes About Enterprise Software",
    excerpt: "Software is shifting from tools people operate to systems that operate themselves. Here is what that means for enterprise architecture.",
    takeaway: "Key takeaway: the winners in agentic AI will be defined by clean architecture — governed APIs, observable workflows and clear human approval points — not by who writes the most prompts.",
    body: [
      "For three decades, enterprise software has been built around a simple assumption: humans operate tools. Agentic AI inverts that assumption. Instead of waiting for instruction at every step, intelligent agents can pursue goals — reasoning over context, choosing tools, and executing multi-step work across business systems.",
      "This changes how enterprise software is designed. Interfaces matter less; orchestration matters more. The critical questions become: which systems can an agent access, what decisions can it make autonomously, and where must human approval sit in the loop?",
      "The enterprises that win with agentic AI will not be the ones with the most prompts. They will be the ones with the cleanest architecture — well-defined APIs, governed data, observable workflows, and clear boundaries between autonomous action and human judgment.",
    ],
  },
  {
    category: "Automation",
    title: "Why AI Automation Needs Architecture, Not Just Prompts",
    excerpt: "A prompt is not a system. Durable automation requires orchestration, governance and observability — the same disciplines as any production software.",
    takeaway: "Key takeaway: treat every AI agent as a production software component — versioned, monitored, auditable — and automation survives contact with the real business.",
    body: [
      "It is easy to build an AI demo. It is hard to build AI that runs a business process reliably, every day, at scale. The difference is architecture.",
      "Production-grade automation needs the same engineering disciplines as any critical system: versioned workflows, retry and fallback logic, access control, audit trails, evaluation harnesses, and monitoring that tells you when an agent's behavior drifts.",
      "At JIYONOX, we treat every agent as a software component inside a larger operating system — connected to real systems through governed APIs, measured against business outcomes, and designed so humans can supervise, override and improve it over time.",
    ],
  },
  {
    category: "Artificial Intelligence",
    title: "Building AI Systems That Can Actually Operate a Business",
    excerpt: "Moving from answering questions to executing work: the architecture behind AI that books, routes, drafts, reconciles and reports.",
    takeaway: "Key takeaway: operational AI needs three layers — intelligence, orchestration and integration — engineered as one system before agents can safely execute real work.",
    body: [
      "Most AI deployments stop at conversation. A chatbot answers; a human still acts. Real operational leverage begins when AI systems can execute — creating records, triggering workflows, coordinating across CRM, ERP, calendar and communication tools.",
      "That requires three layers working together: an intelligence layer that understands context and intent, an orchestration layer that sequences actions with guardrails, and an integration layer that connects to the systems where work actually happens.",
      "When these layers are engineered as one system, AI stops being a feature and becomes a workforce — one that operates continuously, escalates when uncertain, and gets measurably better over time.",
    ],
  },
  {
    category: "Digital Experience",
    title: "The Future of Digital Experience Is Intelligent",
    excerpt: "Content platforms are evolving from publishing systems into adaptive, AI-driven experience engines.",
    takeaway: "Key takeaway: the opportunity is not replacing platforms like AEM — it is layering AI into the content supply chain so every experience adapts, measures and improves itself.",
    body: [
      "The traditional digital experience stack — CMS, DAM, analytics, personalization — was built for a world where humans assembled every journey. That world is ending.",
      "Modern experience platforms are becoming intelligent: content enriched automatically with metadata, search that answers rather than lists, personalization driven by real-time signals, and SEO continuously optimized by machines rather than quarterly audits.",
      "For enterprises running platforms like AEM, the opportunity is not to replace the stack but to make it intelligent — layering AI into the content supply chain so every experience adapts, measures and improves itself.",
    ],
  },
  {
    category: "Enterprise Architecture",
    title: "From Digital Transformation to Autonomous Operations",
    excerpt: "The next decade of enterprise technology is not about digitizing processes — it is about processes that run themselves.",
    takeaway: "Key takeaway: autonomy is earned in production — automate one workflow end-to-end, prove governance, measure outcomes, then expand.",
    body: [
      "Digital transformation spent a decade moving businesses from paper to software. The next decade moves them from software to autonomous operations — systems that sense, decide and act with minimal human intervention.",
      "This is not a single technology. It is the convergence of cloud-native infrastructure, connected data, agentic AI and disciplined engineering operations. Each capability compounds the others.",
      "The practical path is incremental: automate one workflow end-to-end, prove the governance model, measure the outcome, then expand. Autonomy is earned in production, not promised in a roadmap.",
    ],
  },
];

export const Insights = () => {
  const [open, setOpen] = useState(null);

  return (
    <section id="insights" data-testid="insights-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHeading
          testId="insights"
          overline="Insights"
          title={<>Thinking about <span className="jx-gradient-text">what comes next.</span></>}
        />

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ARTICLES.map((a, i) => (
            <FadeUp key={a.title} delay={(i % 3) * 0.1} className={i === 0 ? "lg:row-span-1" : ""}>
              <button
                data-testid={`insight-card-${i}`}
                onClick={() => setOpen(a)}
                className="group w-full h-full text-left rounded-3xl bg-white border border-jx-ink/8 p-8 flex flex-col transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_44px_rgb(0,0,0,0.08)] hover:border-jx-magenta/30"
              >
                <div className="flex items-center justify-between">
                  <span className="jx-overline text-jx-purple text-[10px]">{a.category}</span>
                  <ArrowUpRight className="w-4 h-4 text-jx-ink/30 transition-all duration-300 group-hover:text-jx-magenta group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
                <h3 className="mt-5 font-display font-bold text-xl tracking-tight text-jx-ink leading-snug flex-none">{a.title}</h3>
                <p className="mt-4 text-sm text-jx-ink/55 leading-relaxed">{a.excerpt}</p>
                <p className="mt-3 text-xs leading-relaxed text-jx-purple/80 font-medium border-l-2 border-jx-magenta/40 pl-3 flex-1">{a.takeaway}</p>
                <span className="mt-6 text-xs font-bold tracking-widest uppercase text-jx-magenta">Read article</span>
              </button>
            </FadeUp>
          ))}
        </div>
      </div>

      <FadeUp delay={0.15} className="mt-20 max-w-7xl mx-auto px-6 lg:px-8">
        <div data-testid="newsletter-section" className="rounded-3xl bg-jx-mist border border-jx-ink/6 px-8 py-12 sm:px-14 text-center relative overflow-hidden">
          <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 w-96 h-40 jx-gradient-bg opacity-10 blur-3xl" />
          <p className="jx-overline text-jx-purple">JIYONOX Insights, Monthly</p>
          <h3 className="mt-4 font-display font-extrabold text-2xl sm:text-3xl tracking-tighter text-jx-ink">
            Thinking on agentic AI & intelligent operations — <span className="jx-gradient-text">in your inbox.</span>
          </h3>
          <p className="mt-3 text-sm sm:text-base text-jx-ink/55 max-w-lg mx-auto">One thoughtful email a month. No noise, no spam — unsubscribe anytime.</p>
          <div className="mt-8 relative">
            <NewsletterForm />
          </div>
        </div>
      </FadeUp>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="insight-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-jx-night/60 backdrop-blur-sm"
            onClick={() => setOpen(null)}
          >
            <motion.article
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-white p-8 sm:p-14 relative"
            >
              <button
                data-testid="insight-modal-close"
                onClick={() => setOpen(null)}
                className="absolute top-6 right-6 p-2 rounded-full bg-jx-mist hover:bg-jx-purple/10 transition-colors"
                aria-label="Close article"
              >
                <X className="w-5 h-5 text-jx-ink" />
              </button>
              <span className="jx-overline text-jx-purple">{open.category}</span>
              <h3 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl tracking-tighter text-jx-ink leading-tight">{open.title}</h3>
              <div className="mt-8 space-y-6">
                {open.body.map((p, i) => (
                  <p key={i} className={`leading-relaxed ${i === 0 ? "text-lg text-jx-ink/80 font-medium" : "text-base text-jx-ink/65"}`}>{p}</p>
                ))}
              </div>
              <p className="mt-10 pt-6 border-t border-jx-ink/8 text-xs font-bold tracking-[0.25em] uppercase text-jx-ink/40">JIYONOX Insights</p>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
